import { getDatabase, ref, set, get, query, orderByChild, startAt, endAt, increment } from 'firebase/database';
import { QRScan } from '../types/analytics';

interface QRScanInput {
  tableNumber: string;
  userAgent: string;
  timestamp: number;
  sessionId: string;
}

export const trackQRScan = async (data: QRScanInput): Promise<void> => {
  try {
    const db = getDatabase();
    const scanRef = ref(db, `qr_scans/scans/${Date.now()}`);
    const dailyRef = ref(db, `qr_scans/daily/${new Date().toISOString().split('T')[0]}/${data.tableNumber}`);
    
    await Promise.all([
      // Track detailed scan with mode
      set(scanRef, {
        ...data,
        scanTime: Date.now(),
        timestamp: Date.now(),
        mode: 'order' // Add mode to tracking data
      }),
      // Increment daily counter
      set(dailyRef, increment(1))
    ]).catch((error) => {
      // Tracking must never break the ordering flow, but the failure is
      // surfaced in the console instead of being swallowed silently.
      console.debug('QR scan tracking failed (non-fatal):', error);
    });
  } catch (error) {
    console.debug('QR scan tracking failed (non-fatal):', error);
  }
};

export const getQRScanStats = async (timeframe: { start: Date; end: Date }): Promise<QRScan[]> => {
  const db = getDatabase();
  const scansRef = ref(db, 'qr_scans/scans');
  const scansQuery = query(
    scansRef,
    orderByChild('timestamp'),
    startAt(timeframe.start.getTime()),
    endAt(timeframe.end.getTime())
  );
  
  const snapshot = await get(scansQuery);
  const stats: { 
    [key: string]: {
      tableId: string;
      count: number;
      lastScan: Date;
      uniqueVisitors: Set<string>;
      revenue: number;
    } 
  } = {};
  
  snapshot.forEach((child) => {
    const scan = child.val() as QRScanInput;
    if (!stats[scan.tableNumber]) {
      stats[scan.tableNumber] = {
        tableId: scan.tableNumber,
        count: 0,
        lastScan: new Date(scan.timestamp),
        uniqueVisitors: new Set<string>(),
        revenue: 0
      };
    }
    stats[scan.tableNumber].count++;
    stats[scan.tableNumber].uniqueVisitors.add(scan.sessionId);
  });
  
  // Convert Set size to number for uniqueVisitors
  return Object.values(stats).map(stat => ({
    ...stat,
    uniqueVisitors: stat.uniqueVisitors.size,
    lastScan: stat.lastScan
  }));
};

/**
 * Unique sessions that scanned ANY table within the last `period` hours.
 */
export const getTotalUniqueVisitors = async (period = 24): Promise<number> => {
  const db = getDatabase();
  const scansRef = ref(db, 'qr_scans/scans');
  const timeThreshold = Date.now() - period * 60 * 60 * 1000;
  
  const scansQuery = query(
    scansRef,
    orderByChild('timestamp'),
    startAt(timeThreshold)
  );
  
  const snapshot = await get(scansQuery);
  const uniqueSessions = new Set<string>();
  
  snapshot.forEach((child) => {
    const scan = child.val() as QRScanInput;
    uniqueSessions.add(scan.sessionId);
  });
  
  return uniqueSessions.size;
}; 